import { api } from '@goaltree/backend/convex/_generated/api'
import { JOIN_MESSAGE_MAX } from '@goaltree/backend/convex/lib/groveShowcase'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useConvexAuth, useMutation, useQuery } from 'convex/react'
import { CheckCircle2, Leaf, Loader2 } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { GroveIcon } from '@/components/groves/grove-icon'
import { toastGroveError } from '@/components/groves/grove-utils'
import { StreakFlame } from '@/components/streak-icons'
import { optimisticRequestToJoin } from '@/lib/groves-optimistic'
import { clearReturnTo, stashReturnTo } from '@/lib/return-to'

export const Route = createFileRoute('/groves/request/$slug')({
  head: () => ({
    meta: [{ title: 'Ask to join — Goaltree' }, { name: 'robots', content: 'noindex, nofollow' }],
  }),
  component: RouteComponent,
})

const primaryBtn =
  'flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full bg-primary text-[15px] font-extrabold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.99] disabled:pointer-events-none disabled:opacity-50'

function RouteComponent() {
  const { t } = useTranslation()
  const { slug } = Route.useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const grove = useQuery(api.groveShowcase.getGroveShowcase, { slug })
  const viewer = useQuery(api.groveShowcase.viewerStatus, isLoading ? 'skip' : { slug })
  const request = useMutation(api.groveShowcase.requestToJoin).withOptimisticUpdate(
    optimisticRequestToJoin,
  )
  const [message, setMessage] = useState('')
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (isAuthenticated) clearReturnTo()
  }, [isAuthenticated])

  function signIn() {
    const returnTo = `/groves/request/${slug}`
    stashReturnTo(returnTo)
    navigate({ to: '/auth/login', search: { returnTo } })
  }

  async function submit() {
    setSending(true)
    try {
      await request({ slug, message: message.trim() || undefined })
    } catch (err) {
      toastGroveError(err, t)
    } finally {
      setSending(false)
    }
  }

  const loading = grove === undefined || isLoading || viewer === undefined

  return (
    <div className="flex min-h-svh flex-col bg-background">
      <header className="shrink-0 border-b bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 py-3">
          <img src="/favicon.svg?v=4" alt="Goaltree" className="h-7 w-7 select-none" />
          <span className="text-sm font-semibold tracking-tight">Goaltree</span>
        </div>
      </header>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        {loading ? (
          <div className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8">
            <div className="mx-auto size-20 animate-pulse rounded-3xl bg-muted/40" />
            <div className="mx-auto mt-4 h-5 w-2/3 animate-pulse rounded bg-muted/40" />
            <div className="mx-auto mt-2 h-3 w-1/2 animate-pulse rounded bg-muted/30" />
            <div className="mt-6 h-11 animate-pulse rounded-full bg-muted/40" />
          </div>
        ) : grove === null || viewer === null ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <Leaf className="size-10 text-muted-foreground" strokeWidth={1.75} />
            <p className="text-lg font-bold text-foreground">{t('groves.request.invalidTitle')}</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              {t('groves.request.invalidDesc')}
            </p>
            <Link
              to="/"
              className="mt-2 text-sm font-semibold text-primary-text underline-offset-4 hover:underline"
            >
              {t('share.goToGoaltree')}
            </Link>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="w-full max-w-sm rounded-3xl border border-border/50 bg-card px-6 py-8 text-center"
          >
            <GroveIcon icon={grove.icon} size="lg" className="mx-auto" />
            <h1 className="mt-4 text-xl font-extrabold tracking-tight text-foreground">
              {grove.name}
            </h1>
            {grove.description && (
              <p className="mt-1.5 text-sm text-muted-foreground">{grove.description}</p>
            )}
            <div className="mt-3 flex items-center justify-center gap-3 text-xs text-muted-foreground">
              <span>
                {t('groves.membersOf', { count: grove.memberCount, max: grove.maxMembers })}
              </span>
              {grove.groveStreak > 0 && (
                <span className="font-bold text-foreground">
                  <StreakFlame /> {grove.groveStreak}
                </span>
              )}
            </div>
            <div className="mt-6">
              {viewer.status === 'member' && viewer.groveId ? (
                <button
                  type="button"
                  className={primaryBtn}
                  onClick={() =>
                    navigate({ to: '/groves/$groveId', params: { groveId: viewer.groveId! } })
                  }
                >
                  {t('groves.join.open')}
                </button>
              ) : viewer.status === 'requested' ? (
                <div className="flex flex-col items-center gap-1.5 rounded-xl bg-primary/10 px-3 py-3">
                  <CheckCircle2 className="size-5 text-primary-text" />
                  <p className="text-sm font-bold text-foreground">{t('groves.request.sent')}</p>
                  <p className="text-xs text-muted-foreground">{t('groves.request.sentDesc')}</p>
                </div>
              ) : grove.full ? (
                <p className="rounded-xl bg-muted/50 px-3 py-2.5 text-sm font-medium text-muted-foreground">
                  {t('groves.errors.full')}
                </p>
              ) : isAuthenticated ? (
                <div className="space-y-3 text-left">
                  <p className="text-center text-sm text-muted-foreground">
                    {t('groves.request.pitch')}
                  </p>
                  <label className="block">
                    <span className="text-xs font-semibold text-muted-foreground">
                      {t('groves.request.messageLabel')}
                    </span>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      maxLength={JOIN_MESSAGE_MAX}
                      rows={3}
                      placeholder={t('groves.request.messagePlaceholder')}
                      className="mt-1 w-full resize-none rounded-xl border border-border/60 bg-muted/20 px-3 py-2 text-sm text-foreground outline-none focus:border-primary/60"
                    />
                  </label>
                  <button type="button" className={primaryBtn} disabled={sending} onClick={submit}>
                    {sending && <Loader2 className="size-4 animate-spin" />}
                    {t('groves.request.submit')}
                  </button>
                </div>
              ) : (
                <button type="button" className={primaryBtn} onClick={signIn}>
                  {t('groves.request.signIn')}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </main>
    </div>
  )
}
